import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Copy } from 'lucide-react';
import { useTheme } from '../theme';
import { binaryToBase64, base64ToBinary } from '../lib/binary';
import { logAnalyticsEvent } from '../firebase';

type Mode = 'bin2b64' | 'b642bin';

export default function BinaryBase64Tool() {
  const { themeClasses } = useTheme();
  const [mode, setMode] = useState<Mode>('bin2b64');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!input.trim()) {
      setOutput('');
      setError('');
      return;
    }
    try {
      setOutput(mode === 'bin2b64' ? binaryToBase64(input) : base64ToBinary(input));
      setError('');
    } catch (err: any) {
      setError(err?.message || 'Conversion failed');
      setOutput('');
    }
  }, [input, mode]);

  const swap = () => {
    setMode((m) => (m === 'bin2b64' ? 'b642bin' : 'bin2b64'));
    setInput(output && !error ? output : '');
    logAnalyticsEvent('binary_switch_mode', {});
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      /* ignore */
    }
  };

  const inLabel = mode === 'bin2b64' ? 'Binary input' : 'Base64 input';
  const outLabel = mode === 'bin2b64' ? 'Base64 output' : 'Binary output';
  const placeholder = mode === 'bin2b64' ? 'e.g. 01001000 01101001' : 'e.g. SGk=';

  return (
    <div className={`${themeClasses.cardBg} rounded-3xl border p-5 sm:p-8`}>
      <div className="flex items-center justify-center mb-6">
        <button
          onClick={swap}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold ${themeClasses.buttonSecondary} border transition-all hover:scale-105`}
        >
          {mode === 'bin2b64' ? 'Binary → Base64' : 'Base64 → Binary'}
          <ArrowRightLeft size={16} />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className={`block text-sm font-semibold mb-2 ${themeClasses.textPrimary}`}>{inLabel}</label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            aria-label={inLabel}
            className={`w-full h-40 p-3 ${themeClasses.inputBg} border rounded-xl resize-none break-all`}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={`block text-sm font-semibold ${themeClasses.textPrimary}`}>{outLabel}</label>
            {output && (
              <button onClick={copy} className={`flex items-center gap-1 text-sm ${themeClasses.accentText}`}>
                <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
          </div>
          {error ? (
            <div className={`h-40 p-3 ${themeClasses.errorBg} border rounded-xl`}>{error}</div>
          ) : (
            <textarea
              readOnly
              value={output}
              aria-label={outLabel}
              className={`w-full h-40 p-3 ${themeClasses.inputBg} border rounded-xl resize-none break-all`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
